import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';

import { TREND_TABS, type DashboardData, type TrendTab } from './dashboardData';
import { TREND_HEIGHT, TrendLine } from './TrendLine';

interface EngagementTrendCardProps {
  /** Absent while loading. */
  series?: DashboardData['series'];
}

/**
 * The daily trend, one tab per category.
 *
 * Every tab has a panel, and only the selected one is shown, so each tab's
 * `aria-controls` names an element that exists. MUI's tabs give arrow-key
 * movement between them.
 */
export function EngagementTrendCard({ series }: EngagementTrendCardProps) {
  const [tab, setTab] = useState<TrendTab>('overview');
  const id = useId();
  const tabId = (name: TrendTab) => `${id}-tab-${name}`;
  const panelId = (name: TrendTab) => `${id}-panel-${name}`;
  const headingId = `${id}-heading`;

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={headingId}
      sx={{ p: 2, minWidth: 0 }}
    >
      <Typography id={headingId} variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        Engagement Trend
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Daily measurable activity
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, next: TrendTab) => setTab(next)}
        aria-label="Engagement categories"
        variant="scrollable"
        scrollButtons={false}
        sx={{ mt: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        {TREND_TABS.map((option) => (
          <Tab
            key={option.id}
            value={option.id}
            label={option.label}
            id={tabId(option.id)}
            aria-controls={panelId(option.id)}
          />
        ))}
      </Tabs>

      {TREND_TABS.map((option) => (
        <Box
          key={option.id}
          role="tabpanel"
          id={panelId(option.id)}
          aria-labelledby={tabId(option.id)}
          hidden={option.id !== tab}
          sx={{ pt: 2 }}
        >
          {option.id !== tab ? null : series ? (
            <TrendLine label={option.label} points={series[option.id]} />
          ) : (
            <Skeleton variant="rounded" height={TREND_HEIGHT} />
          )}
        </Box>
      ))}
    </Paper>
  );
}
